// components/GroupNoteBanner.jsx — Anamoria SPA
// v1.2 — NEW-163 Increment B (2026-10-30): members can RESPOND to a note. Also, from Sophie's live
//   render 2026-10-30: the "Shared {day} · here until {name} shares the next one" meta line (v1.0/v1.1)
//   took a line of its own; the date now sits in the kicker ("THIS WEEK'S NOTE FROM {NAME} · SHARED
//   OCTOBER 25", leader: "YOUR NOTE TO THE GROUP · SHARED OCTOBER 25") and the "here until …" sentence
//   is dropped. Copy change to an Increment A line, recorded in the copy file at next version. A terracotta "Respond"
//   on the current note and a small outline "Respond" on each earlier note toggle an inline action
//   row under that note (plan v0.5 §9.2 decision 1a; mockup v0.11): compact pills Record / Write /
//   Photo / Video (Video only when videoEnabled; Photo = the same Safari-safe picker as the bottom
//   nav, via utils/contributorCapture.js), each navigating to the capture page with `?note=<id>`.
//   One row open at a time; "Not now" closes it. Pill icons = the MediaTypeChip glyphs (provisional,
//   cosmetic). Member meta line (copy B-7) ships here. Copy: Frontend-UI/…IncrementB_Copy_v1.0.md
//   (response frame: B-1, B-2, B-3, B-3e, B-4, B-5, B-7). New props: canReply, spaceId, videoEnabled.
//   Leader (canShare) is unchanged — no Respond. Without canReply the banner is byte-identical to v1.1.
//   Needs a Router (useNavigate) — both callers (ContributorFeedPage, SpacePage) are inside one.
// v1.1 — 2026-10-29: leader reads her own note in the second person ("Your note to the group",
//   "here until you share the next one"); members keep "This week's note from {name}". Sophie, live render.
// v1.0 — NEW-162 Increment A (2026-10-29): the leader's note to a grief group, shown as a feed
//   banner at the top of the shared group space. Mockup Frontend-UI/Anamoria_GriefGroup_SharedSpace_Mockup_v0.8.
//   - Leader (canShare): "Share a new note" opens an inline composer; posting is immediate, no admin
//     approval (POST /leader/note — the caller supplies onShare). The current note moves to "Earlier notes".
//   - Member (read-only): the current note + a collapsed "Earlier notes" list.
//   - Nothing renders for a member when no note exists yet; the leader sees a quiet invitation.
//   One-way in this increment: no Reply button (Increment B). Presentational + local composer state only;
//   the caller gates on group space + group_spaces_enabled and owns the data (groupNotes from the space GET).
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './GroupNoteBanner.module.css';
import { Glyph } from './MediaTypeChip';   // v1.2: the Respond row reuses the feed-chip glyphs
import { captureTargets, openPhotoPicker, photoFileFromChange } from '../utils/contributorCapture';   // v1.2

export const NOTE_MAX_CHARS = 1000;
const EARLIER_CAP = 20;

/** Gentle relative day: "today", "yesterday", a weekday within the last six days, else "12 Oct". */
export function noteDayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const days = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** v1.2: "October 25" for the kicker (the kicker is upper-cased by CSS). Empty for an unparseable date. */
export function noteLongDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

/** First name of the leader (fallback "your leader"). v1.2: exported for RespondingToNote / GroupMemoryCard. */
export function firstName(name) {
  return (name || '').trim().split(/\s+/)[0] || 'your leader';
}

export default function GroupNoteBanner({ groupNotes, leaderName, canShare = false, onShare, canReply = false, spaceId, videoEnabled = false }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // v1.2: which note's Respond row is open (a note id), or null. One at a time.
  const [respondingTo, setRespondingTo] = useState(null);
  const photoInputRef = useRef(null);
  const navigate = useNavigate();
  // v1.2: "Earlier notes" is controlled so a click/tap anywhere outside it, or Escape, closes it
  // (Sophie, live render: re-finding the link to close it was a pain).
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyRef = useRef(null);
  useEffect(() => {
    if (!historyOpen) return undefined;
    const onDown = (e) => { if (historyRef.current && !historyRef.current.contains(e.target)) setHistoryOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setHistoryOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [historyOpen]);

  const current = groupNotes?.current || null;
  const earlier = (groupNotes?.earlier || []).slice(0, EARLIER_CAP);
  const name = firstName(current?.authorName || leaderName);
  // A member can respond only when the caller says so AND they are not the leader.
  const showRespond = canReply && !canShare;

  // Members: nothing at all until a note exists. Leader: a quiet invitation.
  if (!current && !canShare) return null;

  /* v1.2 — the inline Respond row (copy B-3 / B-3e / B-4 / B-5). Photo opens the hidden input
     synchronously in the click (Safari gesture chain); the File travels in location.state exactly
     as the bottom nav does, and the note id travels in the URL (`?note=`) so it survives refresh. */
  function renderRespondRow(note, isCurrent) {
    if (!showRespond || respondingTo !== note.id) return null;
    const to = captureTargets({ spaceId, note: note.id });
    const who = firstName(note.authorName || leaderName);
    return (
      <div className={styles.replyRow} data-testid="respond-row">
        <p className={styles.lead}>
          Responding to <b>{who}'s note{isCurrent ? '' : ` from ${noteDayLabel(note.createdAt)}`}</b>.
        </p>
        <div className={styles.acts}>
          <button type="button" className={styles.act} onClick={() => navigate(to.record)}><Glyph category="voice" size={15} />Record</button>
          <button type="button" className={styles.act} onClick={() => navigate(to.write)}><Glyph category="text" size={15} />Write</button>
          <button type="button" className={styles.act} onClick={() => openPhotoPicker(photoInputRef.current)}><Glyph category="photo" size={15} />Photo</button>
          {videoEnabled && (
            <button type="button" className={styles.act} onClick={() => navigate(to.video)}><Glyph category="video" size={15} />Video</button>
          )}
        </div>
        <div className={styles.rowFoot}>
          <button type="button" className={styles.cancel} onClick={() => setRespondingTo(null)}>Not now</button>
        </div>
      </div>
    );
  }

  function handlePhotoFileChange(e) {
    const file = photoFileFromChange(e);
    if (!file || !respondingTo) return; // cancelled → stay here
    navigate(captureTargets({ spaceId, note: respondingTo }).photo, { state: { file } });
  }

  async function share() {
    const body = text.trim();
    if (!body) { setError("Write a few words first, whenever you're ready."); return; }
    if ([...body].length > NOTE_MAX_CHARS) { setError(`Notes can hold up to ${NOTE_MAX_CHARS.toLocaleString()} characters.`); return; }
    setBusy(true); setError('');
    try {
      await onShare?.(body);
      setText(''); setOpen(false);
    } catch {
      setError("We weren't able to share that note. When you're ready, please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.wrap} aria-label="Note from the group leader" data-testid="group-note-banner">
      {current ? (
        <div className={styles.banner}>
          <div className={styles.main}>
            <div className={styles.kick}>
              {canShare ? 'Your note to the group' : `This week's note from ${name}`}
              {noteLongDate(current.createdAt) && <span className={styles.kickDate}> · Shared {noteLongDate(current.createdAt)}</span>}
            </div>
            <p className={styles.text}>{current.body}</p>
            {renderRespondRow(current, true)}
          </div>
          {canShare && !open && (
            <button type="button" className={styles.ghost} onClick={() => setOpen(true)}>Share a new note</button>
          )}
          {showRespond && respondingTo !== current.id && (
            <button type="button" className={styles.ghost} aria-label={`Respond to ${name}'s note`} onClick={() => setRespondingTo(current.id)}>Respond</button>
          )}
        </div>
      ) : (
        <div className={`${styles.banner} ${styles.empty}`}>
          <div className={styles.main}>
            <div className={styles.kick}>A note for the group</div>
            <p className={styles.text}>When you're ready, share a first note with the group.</p>
          </div>
          {!open && (
            <button type="button" className={styles.ghost} onClick={() => setOpen(true)}>Share a note</button>
          )}
        </div>
      )}


      {canShare && open && (
        <div className={styles.composer}>
          <label className={styles.label} htmlFor="group-note-body">Share a note with the group</label>
          <textarea
            id="group-note-body"
            className={styles.textarea}
            value={text}
            maxLength={NOTE_MAX_CHARS}
            onChange={(e) => { setText(e.target.value); if (error) setError(''); }}
            placeholder="Offer a prompt, a few words, or a reading. There is no need to ask for a reply."
            disabled={busy}
          />
          {error && <p className={styles.error} role="alert">{error}</p>}
          <div className={styles.row}>
            <span className={styles.hint}>Your note goes to the group right away and takes the place of the one above. Earlier notes stay.</span>
            <div className={styles.actions}>
              <button type="button" className={styles.ghostDark} onClick={() => { setOpen(false); setText(''); setError(''); }} disabled={busy}>Cancel</button>
              <button type="button" className={styles.primary} onClick={share} disabled={busy}>{busy ? 'Sharing…' : 'Share'}</button>
            </div>
          </div>
        </div>
      )}

      {/* v1.2: one row under the card — member meta line (B-7) on the left, "Earlier notes" link on the
          right; the open notes box then takes the full row. Costs no vertical space when collapsed. */}
      {(earlier.length > 0 || (current && showRespond)) && (
      <div className={styles.below}>
      {current && showRespond && (
        <p className={styles.meta}>You can share a memory without responding. Responding connects it to {name}'s note.</p>
      )}
      {earlier.length > 0 && (
        <details className={styles.history} ref={historyRef} open={historyOpen} onToggle={(e) => setHistoryOpen(e.currentTarget.open)}>
          <summary className={styles.summary}>Earlier notes <span className={styles.count}>· {earlier.length}</span></summary>
          <div>
            {earlier.map((n) => (
              <div key={n.id} className={styles.past}>
                <div className={styles.pastRow}>
                  <p className={styles.pastText}>{n.body}</p>
                  {showRespond && respondingTo !== n.id && (
                    <button type="button" className={styles.replyBtn} aria-label={`Respond to ${firstName(n.authorName || leaderName)}'s note from ${noteDayLabel(n.createdAt)}`} onClick={() => setRespondingTo(n.id)}>Respond</button>
                  )}
                </div>
                <span className={styles.pastMeta}>Shared {noteDayLabel(n.createdAt)}</span>
                {renderRespondRow(n, false)}
              </div>
            ))}
          </div>
        </details>
      )}
      </div>
      )}

      {/* v1.2: one hidden file input for the Respond rows' Photo (clicked synchronously; see renderRespondRow). */}
      {showRespond && (
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className={styles.fileInput}
          onChange={handlePhotoFileChange}
          data-testid="respond-photo-input"
        />
      )}
    </section>
  );
}
